import i18n from '@/i18n';
import { dataParaExibir, FUSO_SP } from '@pps/datas-sp';

type ConstantItem = { value: string; label: string; labelKey: string };
type ActivityItem = ConstantItem & { icon: string };

function translated<T extends { value: string; labelKey: string }>(items: T[]): (T & { label: string })[] {
  return items.map(item => ({
    ...item,
    get label() { return i18n.t(item.labelKey); },
  }));
}

export const LEAD_SOURCES: ConstantItem[] = translated([
  { value: 'indicacao', labelKey: 'constants.sources.indicacao' },
  { value: 'site', labelKey: 'constants.sources.site' },
  { value: 'whatsapp', labelKey: 'constants.sources.whatsapp' },
  { value: 'walk_in', labelKey: 'constants.sources.walk_in' },
  { value: 'agregador', labelKey: 'constants.sources.agregador' },
  { value: 'instagram', labelKey: 'constants.sources.instagram' },
  // Leads criados via link publico de autocadastro do professor autonomo
  // (FASE P1.2). Registrado aqui so pra manter o label traduzido quando um
  // desses leads aparece em telas genericas (ex.: /leads acessada por URL).
  { value: 'autocadastro', labelKey: 'constants.sources.autocadastro' },
  // Leads criados/enriquecidos pelo webhook de atribuicao do Tintim (Frente 1).
  { value: 'tintim', labelKey: 'constants.sources.tintim' },
  // Convidado adicionado na chamada de uma aula (AddAvulsoDialog, spec
  // "avulso na presenca" 2026-08-19). Sem esta entrada, o LeadDetailDrawer
  // renderizava origem vazia e o filtro de origem do Pipeline nao achava.
  { value: 'convidado', labelKey: 'constants.sources.convidado' },
]);

// Espelha o catálogo public.sports (mesma ordem do display_order de lá). Esta
// lista é estática porque alimenta selects de texto livre (ex.: specialty de
// club_teachers), que gravam o slug e não o sport_id — telas que gravam FK usam
// o catálogo do banco direto (useClubSports/useSportName). Ao adicionar uma
// modalidade no banco, adicione aqui e nos locales (constants.sports.*).
export const SPORTS: ConstantItem[] = translated([
  { value: 'tenis', labelKey: 'constants.sports.tenis' },
  { value: 'beach_tennis', labelKey: 'constants.sports.beach_tennis' },
  { value: 'padel', labelKey: 'constants.sports.padel' },
  { value: 'squash', labelKey: 'constants.sports.squash' },
  { value: 'futevolei', labelKey: 'constants.sports.futevolei' },
  { value: 'volei', labelKey: 'constants.sports.volei' },
  { value: 'volei_de_praia', labelKey: 'constants.sports.volei_de_praia' },
  { value: 'frescobol', labelKey: 'constants.sports.frescobol' },
  { value: 'altinha', labelKey: 'constants.sports.altinha' },
  { value: 'beach_soccer', labelKey: 'constants.sports.beach_soccer' },
  { value: 'beach_handball', labelKey: 'constants.sports.beach_handball' },
  { value: 'futebol', labelKey: 'constants.sports.futebol' },
  { value: 'academia', labelKey: 'constants.sports.academia' },
  { value: 'musculacao', labelKey: 'constants.sports.musculacao' },
  { value: 'treinamento_funcional', labelKey: 'constants.sports.treinamento_funcional' },
  { value: 'cross_training', labelKey: 'constants.sports.cross_training' },
  { value: 'personal_trainer', labelKey: 'constants.sports.personal_trainer' },
  { value: 'pilates', labelKey: 'constants.sports.pilates' },
  { value: 'yoga', labelKey: 'constants.sports.yoga' },
  { value: 'natacao', labelKey: 'constants.sports.natacao' },
]);

// Slug ('volei_de_praia') → label traduzido ('Vôlei de Praia'). Fallback no
// próprio valor: specialty de club_teachers é texto livre no banco — valores
// fora do catálogo (importações antigas) exibem como estão em vez de sumir.
export function sportLabel(value: string | null | undefined): string | null {
  if (!value) return null;
  return SPORTS.find(s => s.value === value)?.label ?? value;
}

export const PRODUCT_TYPES: ConstantItem[] = translated([
  { value: 'aula_avulsa', labelKey: 'constants.productTypes.aula_avulsa' },
  { value: 'pacote_mensal', labelKey: 'constants.productTypes.pacote_mensal' },
  { value: 'plano_trimestral', labelKey: 'constants.productTypes.plano_trimestral' },
  { value: 'plano_semestral', labelKey: 'constants.productTypes.plano_semestral' },
  { value: 'plano_anual', labelKey: 'constants.productTypes.plano_anual' },
  { value: 'reserva_quadra', labelKey: 'constants.productTypes.reserva_quadra' },
  { value: 'evento', labelKey: 'constants.productTypes.evento' },
  { value: 'plano_locacao', labelKey: 'constants.productTypes.plano_locacao' },
  { value: 'torneio', labelKey: 'constants.productTypes.torneio' },
]);

// Tipos que se materializam como reserva real de quadra na conversão
// (convert-lead mode 'booking'). 'evento' = reserva_quadra + multi-quadra +
// nome do evento carimbado em cada booking.
// NOTA: 'plano_locacao' NÃO entra aqui — os consumidores de booking-like
// assumem 1 booking_date único (drawer simples + convertBookingDirect);
// locação é série recorrente com fluxo próprio (mode 'rental_plan').
export const BOOKING_LIKE_PRODUCT_TYPES = ['reserva_quadra', 'evento'] as const;
export function isBookingLikeProductType(pt: string | null | undefined): boolean {
  return !!pt && (BOOKING_LIKE_PRODUCT_TYPES as readonly string[]).includes(pt);
}

/** Plano de locação: série recorrente multi-quadra (assistente próprio). */
export const RENTAL_PLAN_PRODUCT_TYPE = 'plano_locacao';
export function isRentalPlanProductType(pt: string | null | undefined): boolean {
  return pt === RENTAL_PLAN_PRODUCT_TYPE;
}

/** Qualquer tipo que vira reserva de quadra no ganho (booking-like OU locação). */
export function isCourtProductType(pt: string | null | undefined): boolean {
  return isBookingLikeProductType(pt) || isRentalPlanProductType(pt);
}

export const PLAN_TYPES: ConstantItem[] = translated([
  { value: 'mensal', labelKey: 'constants.planTypes.mensal' },
  { value: 'trimestral', labelKey: 'constants.planTypes.trimestral' },
  { value: 'semestral', labelKey: 'constants.planTypes.semestral' },
  { value: 'anual', labelKey: 'constants.planTypes.anual' },
  { value: 'day_use', labelKey: 'constants.planTypes.day_use' },
  { value: 'diaria_avulsa', labelKey: 'constants.planTypes.diaria_avulsa' },
  { value: 'pacote_aulas', labelKey: 'constants.planTypes.pacote_aulas' },
]);

/**
 * Aula avulsa: venda de UMA cobrança, não de uma mensalidade. Na conversão
 * não há "início do plano" nem "dia do mês": só o vencimento daquela cobrança.
 */
export const AVULSA_PRODUCT_TYPE = 'aula_avulsa';
export function isAvulsaProductType(pt: string | null | undefined): boolean {
  return pt === AVULSA_PRODUCT_TYPE;
}

/**
 * Quais `plan_type` cada produto de plano pode receber. FONTE ÚNICA para o
 * drawer da oportunidade e para o diálogo de conversão do grid — antes só o
 * drawer filtrava, e o grid oferecia plano mensal numa aula avulsa (e vice-
 * versa). Produto fora do mapa (reserva, evento, locação, torneio) não tem
 * plano; `plansForProductType` devolve a lista inteira para esses casos, que
 * é o comportamento que os call sites já tinham.
 */
export const PRODUCT_TYPE_TO_PLAN_TYPE: Record<string, readonly string[]> = {
  aula_avulsa: ['day_use', 'diaria_avulsa', 'pacote_aulas'],
  pacote_mensal: ['mensal'],
  plano_trimestral: ['trimestral'],
  plano_semestral: ['semestral'],
  plano_anual: ['anual'],
};

export function plansForProductType<T extends { plan_type: string }>(
  plans: readonly T[],
  productType: string | null | undefined,
): T[] {
  const allowed = productType ? PRODUCT_TYPE_TO_PLAN_TYPE[productType] : undefined;
  if (!allowed) return [...plans];
  return plans.filter((p) => allowed.includes(p.plan_type));
}

export const ACTIVITY_TYPES: ActivityItem[] = translated([
  { value: 'note', labelKey: 'constants.activityTypes.note', icon: '📝' },
  { value: 'call', labelKey: 'constants.activityTypes.call', icon: '📞' },
  { value: 'email', labelKey: 'constants.activityTypes.email', icon: '📧' },
  { value: 'whatsapp', labelKey: 'constants.activityTypes.whatsapp', icon: '💬' },
  { value: 'meeting', labelKey: 'constants.activityTypes.meeting', icon: '🤝' },
  { value: 'status_change', labelKey: 'constants.activityTypes.status_change', icon: '🔄' },
]);

// IR-TS-004: valores alinhados ao CHECK de `leads.level` + `club_students.level`
// ('beginner' | 'intermediate' | 'advanced' | 'pro'). PR-A de #373 fez backfill
// PT→EN em leads.level; PR-B adiciona o CHECK; este PR-C unifica o frontend.
// Reusa o bloco i18n `constants.studentLevels.*` (criado no PR #364) para evitar
// duplicação — IR-UX-002 (pt + en alinhados).
export const LEVELS: ConstantItem[] = translated([
  { value: 'beginner',     labelKey: 'constants.studentLevels.beginner' },
  { value: 'intermediate', labelKey: 'constants.studentLevels.intermediate' },
  { value: 'advanced',     labelKey: 'constants.studentLevels.advanced' },
  { value: 'pro',          labelKey: 'constants.studentLevels.pro' },
]);

export function getDaysInStage(createdAt: string | null, lastActivityAt: string | null): number {
  if (!createdAt) return 0;
  const ref = lastActivityAt || createdAt;
  const diffMs = Date.now() - new Date(ref).getTime();
  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
}

export function isColdLead(lastActivityAt: string | null, thresholdDays = 7): boolean {
  if (!lastActivityAt) return true;
  return getDaysInStage(lastActivityAt, null) >= thresholdDays;
}

// IR-UX-005 — `i18n.language` é `'pt' | 'en'`. O locale BCP-47 passado ao
// Intl/Intl.DateTimeFormat permanece `'pt-BR'` propositalmente: clube opera
// em formato BR (R$, dd/MM/yyyy, separador `.`/`,` invertidos vs `pt-PT`).
// Não unificar com `'pt'` puro aqui — não é o mesmo conceito.
export function formatCurrency(value: number): string {
  const lng = i18n.language === 'en' ? 'en-US' : 'pt-BR';
  const currency = i18n.language === 'en' ? 'USD' : 'BRL';
  return new Intl.NumberFormat(lng, { style: 'currency', currency }).format(value);
}

/** Casa exatamente uma data civil `YYYY-MM-DD`, sem hora nem fuso. */
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Converte o valor vindo do banco em `Date` para exibição.
 *
 * Colunas `date` do Postgres chegam como `'2026-07-15'`. O JS parseia essa
 * forma como meia-noite **UTC**, e o `Intl` então formata no fuso do
 * navegador — o que joga a data um dia para trás em qualquer fuso negativo.
 * No Brasil (UTC-3), a 1ª quinzena `2026-07-01 → 2026-07-15` era exibida como
 * `30/06/2026 – 14/07/2026`.
 *
 * Por isso datas civis são montadas com os componentes no fuso LOCAL: elas não
 * representam um instante, e sim um dia do calendário. Valores com horário
 * (timestamptz, `created_at`, `paid_at`) seguem pelo parse normal, que aí sim
 * precisa da conversão de fuso.
 */
export function parseDisplayDate(value: string): Date {
  if (DATE_ONLY_RE.test(value)) {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  return new Date(value);
}

/**
 * Data civil para exibição. 'yyyy-MM-dd' sai como veio; um instante
 * (timestamptz) sai no DIA DE SÃO PAULO, não no do navegador (IR-TZ-005, dono
 * 21/10/2026): o pagamento das 22h de Brasília é do dia dele, mesmo num
 * tablet em GMT 0.
 */
export function formatDate(date: string): string {
  const lng = i18n.language === 'en' ? 'en-US' : 'pt-BR';
  if (DATE_ONLY_RE.test(date)) {
    return new Intl.DateTimeFormat(lng, { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' })
      .format(dataParaExibir(date));
  }
  return new Intl.DateTimeFormat(lng, { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: FUSO_SP })
    .format(new Date(date));
}

export function formatDateTime(date: string): string {
  const lng = i18n.language === 'en' ? 'en-US' : 'pt-BR';
  return new Intl.DateTimeFormat(lng, {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }).format(new Date(date));
}

/**
 * ISO (timestamptz) -> valor de `<input type="datetime-local">`
 * (`YYYY-MM-DDTHH:mm`) nos componentes do fuso LOCAL do navegador.
 *
 * E o par correto da gravacao `new Date(value).toISOString()`: o input
 * datetime-local fala horario de parede local. Ler com `.slice(0, 16)` pegava
 * o relogio em UTC — o drawer exibia ~3h a frente do grid (formatDateTime e
 * local) e cada ciclo abrir->salvar deslocava o horario pelo offset do fuso.
 */
export function toDatetimeLocal(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
